// SOLICITUD IA · acceso DIRECTO por enlace (identificador opaco en la URL).
// La URL no lleva nombre, sitio ni el hash de autorización: sólo el id de la solicitud. Exige
// sesión y autorización efectiva (el backend responde 404 indistinguible si no corresponde).
import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import SolicitudPanel from "@/components/ia/SolicitudPanel";
import { Loader2, ArrowLeft, ShieldAlert } from "lucide-react";

export default function SolicitudIA() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [s, setS] = useState(null);
  const [error, setError] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [aplicando, setAplicando] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const { data } = await api.get(`/ia/act/propuesta/${id}`);
      setS(data); setError(null);
    } catch {
      setS(null);
      // Copia NEUTRA y fija: 403 y 404 se presentan igual, sin revelar si la solicitud existe
      // en otra cartera ni exponer el error técnico del cliente HTTP.
      setError("La solicitud no existe, ya no está disponible o tu cuenta no puede revisarla.");
    } finally { setCargando(false); }
  }, [id]);

  useEffect(() => { cargar(); }, [cargar]);

  // Sin reintentos automáticos: caducidad, 409 y pérdida de autorización se enuncian tal cual.
  const confirmar = async () => {
    setAplicando(true);
    try {
      const { data } = await api.post("/ia/act/confirmar", { propuesta_id: s.id, hash: s.hash });
      if (data.aplicado || data.idempotente) toast.success(data.idempotente ? "Ya estaba aplicada" : "Cambio temporal aplicado");
      else toast.error(data.mensaje || data.pregunta || "No se aplicó.");
      await cargar();
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "No se pudo aplicar.");
      await cargar();
    } finally { setAplicando(false); }
  };

  const rechazar = async (motivo) => {
    setAplicando(true);
    try {
      await api.post("/ia/act/rechazar", { propuesta_id: s.id, motivo: motivo || "" });
      toast.success("Solicitud rechazada");
      await cargar();
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "No se pudo rechazar.");
      await cargar();
    } finally { setAplicando(false); }
  };

  return (
    <div data-testid="solicitud-ia-page" className="mx-auto w-full max-w-xl">
      <button type="button" data-testid="solicitud-volver" onClick={() => navigate(-1)}
        className="mb-3 inline-flex min-h-10 items-center gap-1.5 text-xs font-bold text-[#0891A6] hover:underline">
        <ArrowLeft className="h-3.5 w-3.5" /> Volver
      </button>
      <h1 className="text-lg font-extrabold text-[#012472] dark:text-white md:text-lg">Solicitud de cambio temporal de prioridad</h1>
      <p className="mt-1 text-sm text-slate-500 dark:text-slate-300">
        Nada se aplica automáticamente: la decisión es humana y queda registrada.
      </p>

      {cargando && (
        <p data-testid="solicitud-cargando" className="mt-4 inline-flex items-center gap-2 text-sm text-slate-400">
          <Loader2 className="h-4 w-4 animate-spin" /> Cargando…
        </p>
      )}

      {!cargando && error && (
        <p data-testid="solicitud-error" className="mt-4 inline-flex items-start gap-2 rounded-xl border border-[#9B2C2C]/30 bg-[#9B2C2C]/10 px-3 py-2.5 text-sm font-semibold text-[#9B2C2C]">
          <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" /> {error}
        </p>
      )}

      {!cargando && s && (
        <div className="mt-4 rounded-xl border-2 border-[#01CBE3]/60 bg-[#01CBE3]/5 p-3 dark:bg-slate-900/60">
          <SolicitudPanel s={s} prefix="solicitud" aplicando={aplicando}
            onConfirmar={confirmar} onRechazar={rechazar} />
        </div>
      )}
    </div>
  );
}
